// @ts-check
/**
 * The AI Report Explainer for an organisation: the panel chrome plus the Executive Summary laid out by
 * the report components.
 *
 * Opening reads the stored report, and requests a generation when there is none or it is stale - so
 * opening can spend an AI call, though never a second one for a report that is already current. While a
 * generation runs the panel polls, and a previous report is kept on screen if a refresh fails.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuLink, LuRefreshCw, LuSparkles } from 'react-icons/lu';

import AIPanel from '@components/general/aiPanel/AIPanel.jsx';
import AIReportBody from '@components/general/aiPanel/report/AIReportBody.jsx';
import { useAIPanel } from '@components/general/aiPanel/aiPanelContext';
import { TestableButton, TestableText } from '@components/general/TestableComponents';
import useOrganisationAiReport from '@datahooks/useOrganisationAiReport';
import { formatTimeAgo } from '@datahooks/metadata/DataUtils';

/** @typedef {import('@datahooks/ProjectsListContext').BasicProject} BasicProject */

/**
 * Kept together so the four failure lines stay parallel. 'refresh' still has a previous report on
 * screen and must say so; 'initial' has nothing to fall back on. 'unavailable' is not a failure -
 * nothing went wrong, there is just nothing to summarise, e.g. no accessible projects.
 */
export const SUMMARY_MESSAGES = {
  refresh: {
    timedOut: 'This is taking longer than expected. Showing the previous summary.',
    failed: "We couldn't refresh the Executive Summary. Showing the previous summary.",
  },
  initial: {
    timedOut: 'The Executive Summary is taking longer than expected.',
    failed: "We couldn't generate the Executive Summary.",
  },
  unavailable: 'There is no Executive Summary to show.',
  copied: 'Link copied to clipboard',
  copyFailed: 'Link share failed',
  linkedOrgUnavailable: "The link you were sent is for an organisation you don't have access to. Showing the Executive Summary for your organisation instead.",
};

/** How long the copy confirmation stays in the header. */
export const COPY_FEEDBACK_MS = 2000;

/**
 * @param {'refresh'|'initial'} group
 * @param {boolean} hasTimedOut - A timeout reads differently from a failure: nothing is known to be wrong.
 */
const messageFor = (group, hasTimedOut) => SUMMARY_MESSAGES[group][hasTimedOut ? 'timedOut' : 'failed'];

/**
 * @param {Object} props
 * @param {string} [props.organisationName]
 * @param {BasicProject[]} [props.projects] - The projects the report covers. Supplies metriQ's own names,
 *   so the report's links and labels do not depend on what the model called each project, and a count to
 *   show while it loads - the report's own count replaces that once it arrives.
 * @param {boolean} [props.linkedOrgUnavailable] - Opened from a share link for an organisation this user
 *   cannot see, so the panel shows their own and says so.
 */
const OrganisationSummaryPanel = ({ organisationName = '', projects = [], linkedOrgUnavailable = false }) => {
  const { closePanel } = useAIPanel();

  const {
    status,
    report,
    generatedAt,
    isStale,
    changedProjectCount,
    accessibleProjectCount: reportedProjectCount,
    statusMessage,
    isLoading,
    isGenerating,
    hasTimedOut,
    error,
    generateError,
    generate,
    organisationId,
  } = useOrganisationAiReport();

  // Opening generates once when there is nothing stored, or when the stored report is out of date.
  // The ref stops a re-render asking twice.
  const hasRequested = useRef(false);

  useEffect(() => {
    const needsGenerating = status === 'none' || isStale;

    if (!isLoading && needsGenerating && !hasRequested.current) {
      hasRequested.current = true;
      generate().catch(() => {});
    }
  }, [isLoading, status, isStale, generate]);

  // Forced, unlike the open above: the user asking for a refresh overrides the reuse check.
  const runAgain = () => {
    generate({ force: true }).catch(() => {});
  };

  // Confirmation of an action the user just took, so a timed dismiss is right here.
  const [copyState, setCopyState] = useState(/** @type {'idle'|'copied'|'failed'} */ ('idle'));

  useEffect(() => {
    if (copyState === 'idle') return undefined;

    const timer = setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_MS);

    return () => clearTimeout(timer);
  }, [copyState]);

  // Lands on /overview/explainer/:orgId, which OrganisationOverview handles. The button is disabled until
  // the organisation id is known, since a link without it would open the receiver's own organisation.
  const shareLink = () => {
    const shareUrl = `${globalThis.location.origin}/overview/explainer/${organisationId}`;
    const write = globalThis.navigator.clipboard?.writeText(shareUrl);

    if (!write) {
      setCopyState('failed');
      return;
    }

    write.then(() => setCopyState('copied')).catch(() => setCopyState('failed'));
  };

  const hasReport = Boolean(report);
  const isRunning = isLoading || isGenerating || status === 'pending';
  const requestFailed = Boolean(error) || Boolean(generateError) || hasTimedOut || status === 'failed';

  const panelStatus = (() => {
    if (isRunning && !hasTimedOut) {
      return 'loading';
    }

    return requestFailed && !hasReport ? 'error' : 'ready';
  })();

  const projectCount = reportedProjectCount || projects.length;
  const updated = formatTimeAgo(generatedAt);

  const meta = [
    projectCount > 0 && `${projectCount} accessible ${projectCount === 1 ? 'project' : 'projects'}`,
    updated && `updated ${updated}`,
    isStale && changedProjectCount > 0 && `${changedProjectCount} ${changedProjectCount === 1 ? 'project has' : 'projects have'} run since`,
  ].filter(Boolean).join(' · ');

  const contextLine = (
    <>
      <TestableText variant="small" className="text-sm font-semibold text-gray-900 dark:text-gray-100" dataTestId="org-summary-organisation">
        {organisationName}
      </TestableText>
      {meta && (
        <TestableText variant="small" className="text-xs text-gray-500 dark:text-gray-400" dataTestId="org-summary-meta">
          {meta}
        </TestableText>
      )}
      {/* With the organisation, in every state, so a reader knows this is not the report they were sent. */}
      {linkedOrgUnavailable && (
        <TestableText
          variant="small"
          className="mt-2 rounded bg-amber-50 p-2 text-sm font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
          dataTestId="org-summary-linked-org-unavailable"
        >
          {SUMMARY_MESSAGES.linkedOrgUnavailable}
        </TestableText>
      )}
    </>
  );

  const copyFeedback = { idle: '', copied: SUMMARY_MESSAGES.copied, failed: SUMMARY_MESSAGES.copyFailed }[copyState];

  const headerActions = (
    <div className="relative flex items-center">
      {/* Always mounted, so a screen reader hears the text arrive. Out of flow, so the header does not reflow. */}
      <TestableText
        variant="small"
        aria-live="polite"
        className={`absolute right-0 top-full z-10 mt-1 whitespace-nowrap rounded text-xs ${copyFeedback ? "bg-gray-100 px-2 py-0.5 text-gray-700 dark:bg-gray-700 dark:text-gray-200" : ""}`}
        dataTestId="org-summary-share-feedback"
      >
        {copyFeedback}
      </TestableText>
      <TestableButton
        dataTestId="org-summary-share-link"
        variant="text"
        size="sm"
        aria-label="Copy link"
        title="Copy link"
        disabled={isRunning || !organisationId}
        onClick={shareLink}
        className="flex items-center gap-2 px-2 normal-case"
      >
        {copyState === 'copied' ? <LuCheck aria-hidden="true" className="h-4 w-4" /> : <LuLink aria-hidden="true" className="h-4 w-4" />}
      </TestableButton>
    </div>
  );

  const footer = hasReport ? (
    <div className="flex items-center justify-between gap-3">
      <TestableButton
        dataTestId="org-summary-run-again"
        variant="text"
        size="sm"
        disabled={isRunning}
        onClick={runAgain}
        className="flex items-center gap-2 px-2 normal-case"
      >
        <LuRefreshCw aria-hidden="true" className="h-4 w-4" />
        Run again
      </TestableButton>
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-tremor-background-subtle px-2.5 py-1 text-xs font-medium text-tremor-brand dark:bg-gray-700 dark:text-gray-200"
        data-testid="org-summary-ai-badge"
      >
        <LuSparkles aria-hidden="true" className="h-3 w-3" />
        AI-generated
      </span>
    </div>
  ) : null;

  return (
    <AIPanel
      title="AI Report Explainer"
      contextLine={organisationName || linkedOrgUnavailable ? contextLine : null}
      status={panelStatus}
      progressText="Analysing accessible projects and recent trends…"
      announcement={hasReport ? 'Executive Summary ready' : ''}
      headerActions={headerActions}
      footer={footer}
      onClose={closePanel}
    >
      {/* A refresh that failed leaves the previous report on screen, with a note that it is not the new one. */}
      {hasReport && requestFailed && (
        <TestableText
          variant="small"
          className="mb-4 rounded bg-amber-50 p-2 text-sm font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
          dataTestId="org-summary-refresh-warning"
        >
          {messageFor('refresh', hasTimedOut)}
        </TestableText>
      )}

      {/*
        Three outcomes: a report to show, a failure with nothing to fall back on, or nothing
        to summarise at all. The last is not a failure and must not be reported as one.
      */}
      {(() => {
        if (hasReport) {
          return <AIReportBody report={report} projects={projects} />;
        }

        if (requestFailed) {
          return <SummaryFailure hasTimedOut={hasTimedOut} statusMessage={statusMessage} onRunAgain={runAgain} onClose={closePanel} />;
        }

        return <SummaryUnavailable statusMessage={statusMessage} onClose={closePanel} />;
      })()}
    </AIPanel>
  );
};

/**
 * Nothing went wrong and there is nothing to show - an organisation with no accessible projects, for
 * instance. Offers no retry: the generation already ran and would reach the same answer.
 */
const SummaryUnavailable = ({ statusMessage, onClose }) => (
  <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300" data-testid="org-summary-unavailable">
    <TestableText variant="small" className="font-medium text-gray-900 dark:text-gray-100" dataTestId="org-summary-unavailable-headline">{SUMMARY_MESSAGES.unavailable}</TestableText>
    {statusMessage && <TestableText variant="small" className="text-xs text-gray-500 dark:text-gray-400" dataTestId="org-summary-unavailable-message">{statusMessage}</TestableText>}

    <div className="pt-1">
      <TestableButton dataTestId="org-summary-unavailable-back" variant="text" size="sm" className="normal-case" onClick={onClose}>
        Back
      </TestableButton>
    </div>
  </div>
);

SummaryUnavailable.propTypes = {
  statusMessage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

/** Shown only when a generation actually failed and there is no report to fall back on. */
const SummaryFailure = ({ hasTimedOut, statusMessage, onRunAgain, onClose }) => (
  <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300" data-testid="org-summary-error">
    <TestableText variant="small" className="font-medium text-gray-900 dark:text-gray-100" dataTestId="org-summary-error-headline">{messageFor('initial', hasTimedOut)}</TestableText>
    <TestableText variant="small" dataTestId="org-summary-error-reassurance">The Organisation Overview and the data behind it are unchanged.</TestableText>
    {statusMessage && <TestableText variant="small" className="text-xs text-gray-500 dark:text-gray-400" dataTestId="org-summary-error-message">{statusMessage}</TestableText>}

    <div className="flex gap-2 pt-1">
      <TestableButton dataTestId="org-summary-try-again" variant="outlined" size="sm" className="normal-case" onClick={onRunAgain}>
        Try again
      </TestableButton>
      <TestableButton dataTestId="org-summary-back" variant="text" size="sm" className="normal-case" onClick={onClose}>
        Back
      </TestableButton>
    </div>
  </div>
);

SummaryFailure.propTypes = {
  hasTimedOut: PropTypes.bool,
  statusMessage: PropTypes.string,
  onRunAgain: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

OrganisationSummaryPanel.propTypes = {
  organisationName: PropTypes.string,
  linkedOrgUnavailable: PropTypes.bool,
  projects: PropTypes.arrayOf(PropTypes.shape({
    projectId: PropTypes.number,
    title: PropTypes.string,
  })),
};

export default OrganisationSummaryPanel;
